import { cmsConfig } from '$lib/services/config';
import { getCollectionLabel } from '$lib/services/contents/collection';
import { user } from '$lib/services/user/account.svelte';
import { openAuthoring } from '$lib/services/workflow/open-authoring';

/**
 * @import { CommitOptions, FileChange, User } from '$lib/types/private';
 * @import { GitBackend } from '$lib/types/public';
 */

/**
 * Default commit message templates.
 * @see https://decapcms.org/docs/configuration-options/#commit-message-templates
 * @see https://sveltiacms.app/en/docs/backends#commit-messages
 */
const DEFAULT_COMMIT_MESSAGES = {
  create: 'Create {{collection}} “{{slug}}”',
  update: 'Update {{collection}} “{{slug}}”',
  delete: 'Delete {{collection}} “{{slug}}”',
  uploadMedia: 'Upload “{{path}}”',
  deleteMedia: 'Delete “{{path}}”',
  openAuthoring: '{{message}}',
};

/**
 * Markers that tell a CI/CD provider not to build a commit. GitHub Actions and Gitea Actions honour
 * `[skip ci]`, `[ci skip]`, `[no ci]`, `[skip actions]` and `[actions skip]`, GitLab CI honours
 * `[skip ci]` and `[ci skip]`, and Cloudflare Pages accepts the same words hyphenated as well as
 * `[cf-pages-skip]`. They all match case-insensitively anywhere in the message, so this does too:
 * the marker is written as a prefix here, but a commit made elsewhere can carry it in the body.
 * @see https://docs.github.com/en/actions/managing-workflow-runs/skipping-workflow-runs
 * @see https://docs.gitlab.com/ee/ci/pipelines/#skip-a-pipeline
 * @see https://developers.cloudflare.com/pages/platform/branch-build-controls/#skip-builds
 */
const SKIP_CI_REGEX =
  /\[(?:skip[ -](?:ci|actions)|(?:ci|actions)[ -]skip|no[ -]ci|cf-pages-skip)\]/i;

/**
 * Check if a commit message carries a marker that keeps the connected CI/CD provider from building
 * the commit. It says what the author asked for, not what the provider did: a repository with no CI
 * at all never builds an unmarked commit either.
 * @param {string} message Commit message.
 * @returns {boolean} Result.
 */
export const hasSkipCIMarker = (message) => SKIP_CI_REGEX.test(message);

/**
 * Create a Git commit message.
 * @param {FileChange[]} changes File changes to be saved.
 * @param {CommitOptions} options Commit options.
 * @returns {string} Formatted message.
 */
export const createCommitMessage = (
  changes,
  { commitType = 'update', collection, skipCI = undefined },
) => {
  const {
    commit_messages: customCommitMessages = {},
    skip_ci: skipCIEnabled,
    automatic_deployments: autoDeploy,
  } = /** @type {GitBackend} */ (cmsConfig.current?.backend ?? {});

  const { email = '', login = '', name = '' } = /** @type {User} */ (user.account);
  const [firstSlug = ''] = changes.map((item) => item.slug).filter(Boolean);
  const [firstPath, ...remainingPaths] = changes.map(({ path }) => path);
  const collectionLabel = collection ? getCollectionLabel(collection, { useSingular: true }) : '';
  // @ts-ignore
  let message = customCommitMessages[commitType] || DEFAULT_COMMIT_MESSAGES[commitType] || '';

  if (['create', 'update', 'delete'].includes(commitType)) {
    message = message
      .replaceAll('{{slug}}', firstSlug)
      .replaceAll('{{collection}}', collectionLabel)
      .replaceAll('{{path}}', firstPath)
      .replaceAll('{{author-email}}', email)
      .replaceAll('{{author-login}}', login)
      .replaceAll('{{author-name}}', name);
  }

  if (['uploadMedia', 'deleteMedia'].includes(commitType)) {
    message = message
      .replaceAll('{{path}}', firstPath)
      .replaceAll('{{author-email}}', email)
      .replaceAll('{{author-login}}', login)
      .replaceAll('{{author-name}}', name);
  }

  if (remainingPaths.length) {
    message += ` +${remainingPaths.length}`;
  }

  // With Open Authoring the commit is made by an outside contributor, so the message can be wrapped
  // to record who wrote it. The default template is the message on its own, which changes nothing
  if (openAuthoring.current) {
    message = (customCommitMessages.openAuthoring || DEFAULT_COMMIT_MESSAGES.openAuthoring)
      .replaceAll('{{message}}', message)
      .replaceAll('{{author-email}}', email)
      .replaceAll('{{author-login}}', login)
      .replaceAll('{{author-name}}', name);
  }

  // If requested, disable automatic deployments by using the standard `[skip ci]` prefix supported
  // by major CI/CD providers, including GitHub Actions and Cloudflare Pages. To avoid unexpected
  // data retention, deployments for deletion commits are not skipped.
  // https://docs.github.com/en/actions/managing-workflow-runs/skipping-workflow-runs
  // https://docs.gitlab.com/ee/ci/pipelines/#skip-a-pipeline
  // https://developers.cloudflare.com/pages/platform/branch-build-controls/#skip-builds
  if (
    !['delete', 'deleteMedia'].includes(commitType) &&
    // Cannot use the `skipCIEnabled` store here because it leads to an uninitialized store error
    (skipCI ?? (skipCIEnabled === true || autoDeploy === false))
  ) {
    message = `[skip ci] ${message}`;
  }

  return message;
};
